import React, { useMemo, useState } from 'react';
import { marked } from 'marked';
import { ChevronRight } from 'lucide-react';
import { usePortalGet } from './PortalOrdersPage';
import UnderlinedTabs from '../../modules/accounting/QuickBooks/UnderlinedTabs';

// The Terms page (/portal/<name>/terms): the Terms & Conditions of Trade,
// for signed-in Customers only — they aren't on the public site. A Customer
// with credit (a Payment Term of more than 0 days) also sees their Credit
// Terms, on their own tab; nobody else is shown that credit exists. Each
// section folds to its heading and Terminology has its own tab, so the
// page reads as a short list of topics rather than one long document
// (backend/services/portalTermsService.js). The wording is first-party text
// from backend/content/legal.

const CARD_STYLE = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 };
const MUTED = { fontSize: 14, color: '#5a6b85' };
const TERMINOLOGY = 'Terminology';
const PROSE_CSS = `
.portal-terms { font-size: 14px; line-height: 1.6; color: #1f2d3d; max-width: 780px; }
.portal-terms h2 { font-size: 16px; font-weight: 600; margin: 28px 0 8px; }
.portal-terms ol, .portal-terms ul { padding-left: 22px; margin: 0 0 12px; }
.portal-terms ol ol { list-style: lower-alpha; margin-top: 4px; }
.portal-terms li { margin-bottom: 6px; }
.portal-terms table { border-collapse: collapse; margin: 0 0 12px; font-size: 13px; }
.portal-terms th, .portal-terms td { text-align: left; vertical-align: top; padding: 6px 10px 6px 0; border-bottom: 1px solid #eef1f5; }
.portal-terms th { font-weight: 600; color: #5a6b85; }
.portal-terms th:first-child, .portal-terms td:first-child { white-space: nowrap; }
.portal-glossary { margin: 0; }
.portal-glossary > div { display: grid; grid-template-columns: 200px 1fr; gap: 4px 24px; padding: 12px 0; border-bottom: 1px solid #eef1f5; }
.portal-glossary dt { font-weight: 600; }
.portal-glossary dd { margin: 0; color: #3a4a5e; }
@media (max-width: 600px) { .portal-glossary > div { grid-template-columns: 1fr; } }
`;

// The legal text split at its "## " headings: { intro, sections: [{ title,
// html }] }. The Version Change Control table is the business's own record,
// so it never reaches the portal; a Customer is bound by the version they
// signed (Preliminary 3).
const HIDDEN_SECTIONS = ['Version Change Control'];
function splitSections(markdown) {
  const parts = String(markdown || '').split(/^## +(.+)$/m);
  const sections = [];
  for (let i = 1; i < parts.length; i += 2) {
    const title = parts[i].trim();
    if (!HIDDEN_SECTIONS.includes(title)) sections.push({ title, markdown: parts[i + 1] || '', html: marked.parse(parts[i + 1] || '') });
  }
  return { intro: parts[0].trim() ? marked.parse(parts[0]) : '', sections };
}

// Terminology as a glossary: each "**Term** means ..." paragraph becomes a
// term beside its definition, with the leading "means" dropped.
function parseGlossary(markdown) {
  return String(markdown || '').split(/\n\s*\n/).map((para) => {
    const m = para.trim().match(/^\*\*(.+?)\*\*\s+(.*)$/s);
    return m ? { term: m[1], html: marked.parseInline(m[2].replace(/^means\s+/, '')) } : null;
  }).filter(Boolean);
}

function Glossary({ markdown }) {
  return (
    <dl className="portal-terms portal-glossary">
      {parseGlossary(markdown).map(({ term, html }) => (
        <div key={term}>
          <dt>{term}</dt>
          <dd dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      ))}
    </dl>
  );
}

// One section of the terms, folded to its heading until opened. Same
// rotating chevron as the shared Accordion (frontend/src/shared/Accordion.js).
function FoldSection({ title, html }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid #eef1f5' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', padding: '12px 0', cursor: 'pointer', fontSize: 15, fontWeight: 600, color: '#1f2d3d', textAlign: 'left' }}
      >
        <ChevronRight
          aria-hidden="true"
          size={18}
          strokeWidth={2}
          style={{ color: '#9aa5b5', flexShrink: 0, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}
        />
        {title}
      </button>
      {open && <div className="portal-terms" style={{ paddingBottom: 12 }} dangerouslySetInnerHTML={{ __html: html }} />}
    </div>
  );
}

function PortalTermsPage({ portalName, onSessionEnded }) {
  const { data, error } = usePortalGet(portalName, '/terms', null, onSessionEnded, 'Could not load the terms');
  const [tab, setTab] = useState('trade');
  const trade = useMemo(() => splitSections(data?.terms_of_trade.markdown), [data]);
  const credit = useMemo(() => (data?.credit ? splitSections(data.credit.markdown) : null), [data]);
  const terminology = trade.sections.find((sec) => sec.title === TERMINOLOGY);
  const tradeSections = trade.sections.filter((sec) => sec.title !== TERMINOLOGY);
  const tabs = [
    { key: 'trade', label: 'Terms & Conditions of Trade' },
    ...(terminology ? [{ key: 'terminology', label: 'Terminology' }] : []),
    ...(credit ? [{ key: 'credit', label: 'Credit Terms' }] : []),
  ];
  const active = tabs.some((t) => t.key === tab) ? tab : 'trade';

  return (
    <div style={CARD_STYLE}>
      <style>{PROSE_CSS}</style>
      <h1 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 600 }}>Terms</h1>
      {error && <div style={{ color: 'red', fontSize: 13 }}>{error}</div>}
      {!error && !data && <div style={MUTED}>Loading...</div>}
      {data && (
        <>
          <div style={{ margin: '12px 0 16px' }}>
            <UnderlinedTabs tabs={tabs} value={active} onChange={setTab} />
          </div>
          {active === 'trade' && (
            <>
              <div style={{ ...MUTED, fontSize: 13, marginBottom: 8 }}>
                Version {data.terms_of_trade.version} · last updated {data.terms_of_trade.updated}
              </div>
              {trade.intro && <div className="portal-terms" dangerouslySetInnerHTML={{ __html: trade.intro }} />}
              <div style={{ maxWidth: 780 }}>
                {tradeSections.map((sec) => <FoldSection key={sec.title} title={sec.title} html={sec.html} />)}
              </div>
            </>
          )}
          {active === 'terminology' && terminology && (
            <Glossary markdown={terminology.markdown} />
          )}
          {active === 'credit' && credit && (
            <>
              <div style={{ ...MUTED, fontSize: 13, marginBottom: 8 }}>
                Your terms: {data.credit.payment_term}{data.credit.credit_limit ? ` · credit limit ${data.credit.credit_limit}` : ''}
                {' · '}Version {data.credit.version} · last updated {data.credit.updated}
              </div>
              <div className="portal-terms" dangerouslySetInnerHTML={{ __html: credit.intro }} />
              {credit.sections.map((sec) => (
                <div key={sec.title} className="portal-terms" dangerouslySetInnerHTML={{ __html: `<h2>${sec.title}</h2>${sec.html}` }} />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default PortalTermsPage;
